import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Breadcrumb, Text, Separator, Icon, DatePicker } from '@/ui'
import { CSVLink } from 'react-csv'
import { DateTime } from 'luxon'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { ProfitAndLossReports } from '../ProfitAndLossReports'
import { TypographyStyles } from '@/styles'

import {
  DEFAULT_START_DATE,
  DEFAULT_END_DATE,
} from '@/src/constants/businessConstants'
import {
  fetchAdminExpensesBreakdown,
  fetchAdminProfitsBreakdown,
  fetchAdminProfitAndLoss,
  fetchAdminUncategorizedBreakdown,
  fetchAdminOtherBreakdown,
} from '@/src/actions/reportActions'

import {
  fetchAdminExpensesBreakdown as fetchAdminExpensesBreakdownV2,
  fetchAdminProfitsBreakdown as fetchAdminProfitsBreakdownV2,
  fetchAdminProfitAndLoss as fetchAdminProfitAndLossV2,
  fetchAdminUncategorizedBreakdown as fetchAdminUncategorizedBreakdownV2,
  fetchAdminOtherBreakdown as fetchAdminOtherBreakdownV2,
} from '@/src/actions/reportActionsV2'

import { adminFetchSingleUserIfNeeded } from '@/src/actions/admin/adminAllUsersActions'
import { fetchAdminTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { getUserById } from '@/selectors/user.selectors'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { logHeardError } from '@/utils/errorHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

const parseInputDate = (value?: string) => {
  if (!value) {
    return undefined
  }
  const parsed = DateTime.fromFormat(value, DATE_FORMATS_LUXON.INPUT)
  if (!parsed.isValid) {
    logHeardError('AdminProfitAndLoss: invalid date input', { value })
    return undefined
  }
  return parsed
}

export interface ProfitLossLineItem {
  transactionCategoryId: number
  sum: string
}

export const AdminProfitAndLossPanel = () => {
  const { userId: userIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const confirmed = searchParams.get('confirmed') === 'true'
  const navigate = useNavigate()
  const csvLink = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null)

  const userId = Number(userIdParam)
  const dispatch = useAppDispatch()
  const user = useReselector(getUserById, userId)

  const pageTitle = `${confirmed ? 'Journal Entry ' : ''}Profit and Loss`

  const [profitsObj, setProfitsObj] = useState<{
    profits: ProfitLossLineItem[]
    sum: string | number
  }>()
  const [expensesObj, setExpensesObj] = useState<{
    expenses: ProfitLossLineItem[]
    sum: string | number
  }>()
  const [uncategorizedObj, setUncategorizedObj] = useState<{
    sum: number
    transactionCategoryId: number | null
  }>()
  const [otherObj, setOtherObject] = useState<{
    otherExpenses: ProfitLossLineItem[]
    sum: string | number
  }>()
  const [csvData, setCsvData] = useState<string[][]>()
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE)
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE)
  const [csvFetching, setCsvFetching] = useState(false)

  useEffect(() => {
    dispatch(fetchAdminTransactionCategoriesIfNeeded())
    dispatch(adminFetchSingleUserIfNeeded(userId))
  }, [dispatch, userId])

  useEffect(() => {
    const fetch = async () => {
      const params = {
        userId,
        startDate: parseInputDate(startDate),
        endDate: parseInputDate(endDate),
      }

      const [profits, expenses, uncategorized, other] = await Promise.all([
        confirmed
          ? fetchAdminProfitsBreakdownV2(params)()
          : fetchAdminProfitsBreakdown(params)(),
        confirmed
          ? fetchAdminExpensesBreakdownV2(params)(dispatch)
          : fetchAdminExpensesBreakdown(params)(dispatch),
        confirmed
          ? fetchAdminUncategorizedBreakdownV2(params)()
          : fetchAdminUncategorizedBreakdown(params)(),
        confirmed
          ? fetchAdminOtherBreakdownV2(params)()
          : fetchAdminOtherBreakdown(params)(),
      ])

      setProfitsObj(profits)
      setExpensesObj(expenses)
      setUncategorizedObj(uncategorized)
      setOtherObject(other)
    }
    fetch()
  }, [confirmed, dispatch, endDate, startDate, userId])

  useEffect(() => {
    if (csvData && csvLink?.current?.link && !csvFetching) {
      csvLink?.current?.link.click()
    }
  }, [csvData, csvFetching])

  const fetchCSVData = useCallback(async () => {
    setCsvFetching(true)

    const params = {
      userId,
      startDate: parseInputDate(startDate),
      endDate: parseInputDate(endDate),
    }

    const profitAndLoss = confirmed
      ? await fetchAdminProfitAndLossV2(params)()
      : await fetchAdminProfitAndLoss(params)()
    const csv: string[][] = []

    if (profitAndLoss) {
      csv.push([
        `${pageTitle} Statement for:`,
        user?.financialProfile?.businessName || '',
        `${user?.firstName} ${user?.lastName}`,
      ])
      csv.push(['', '', ''])

      csv.push(['Income', '', ''])
      csv.push(['Category', 'Account Type', 'Amount in Dollars'])

      if (profitAndLoss?.profits) {
        profitAndLoss.profits.forEach((profit) => {
          csv.push([profit.name, profit.account_type, profit.sum])
        })
      }

      csv.push(['>> Income Total', '...', profitAndLoss.profitSum.toString()])
      csv.push(['', '', ''])

      csv.push(['Expenses', '', ''])

      csv.push(['Category', 'Account Type', 'Amount in Dollars'])
      if (profitAndLoss?.expenses) {
        profitAndLoss.expenses.forEach((expense) => {
          csv.push([expense.name, expense.account_type, expense.sum])
        })
      }

      csv.push(['', '', ''])
      csv.push([
        '>> Expenses Total',
        '...',
        profitAndLoss.expensesSum.toString(),
      ])
      const netProfit =
        Number(profitAndLoss.profitSum) + Number(profitAndLoss.expensesSum) || 0
      csv.push(['', '', ''])
      csv.push(['Net Profit', '...', netProfit.toString()])

      csv.push(['Other', '', ''])

      csv.push(['Category', 'Account Type', 'Amount in Dollars'])
      if (profitAndLoss?.other) {
        profitAndLoss.other.forEach((other) => {
          csv.push([other.name, other.account_type, other.sum])
        })
      }

      csv.push(['', '', ''])
      csv.push(['>> Other Total', '...', profitAndLoss.otherSum.toString()])
    }

    setCsvData(csv)
    setCsvFetching(false)
  }, [
    confirmed,
    endDate,
    pageTitle,
    startDate,
    user?.financialProfile?.businessName,
    user?.firstName,
    user?.lastName,
    userId,
  ])

  const handleDateRangeChange = useCallback(
    ([startDate, endDate]: [string, string]) => {
      setStartDate(startDate)
      setEndDate(endDate)
    },
    []
  )

  const csvFileName = `${parseInputDate(startDate)?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY) ?? ''}-${parseInputDate(endDate)?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY) ?? ''}-profit-and-loss-${user?.firstName}-${user?.lastName}.csv`

  return (
    <div id="profitAndLossReport">
      <Breadcrumb>
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link
              onClick={() => {
                navigate('/')
              }}
            >
              Dashboard
            </Breadcrumb.Link>
          </Breadcrumb.Item>

          <Breadcrumb.Separator />

          <Breadcrumb.Item>
            <Breadcrumb.Link
              onClick={() => {
                navigate('/admin/finances/list-users')
              }}
            >
              Users
            </Breadcrumb.Link>
          </Breadcrumb.Item>

          <Breadcrumb.Separator />

          <Breadcrumb.Item>
            <Breadcrumb.Link
              onClick={() => {
                navigate(`/admin/finances/records/${userId}`)
              }}
            >
              {user?.firstName} {user?.lastName}
            </Breadcrumb.Link>
          </Breadcrumb.Item>

          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>{pageTitle}</Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>

      <Separator />

      <div className="backdrop">
        <div className=" flex flex-wrap">
          <div className="flex flex-wrap w-full mb-6">
            <Text variant={TypographyStyles.H1}>{pageTitle} Statement</Text>
          </div>
          <div className="w-full flex flex-col md:flex-row justify-between">
            <div className="w-full md:w-1/2">
              <DatePicker
                clearable
                value={[startDate, endDate]}
                onChange={handleDateRangeChange}
                selectsRange
                startDate={startDate}
                endDate={endDate}
                className="w-full md:w-auto"
              />
            </div>
            <div className="w-full md:w-1/2 text-right mt-4 md:mt-0">
              <Button
                loading={csvFetching}
                onClick={fetchCSVData}
                className="w-full md:w-auto"
              >
                <Icon icon={faDownload} className="mr-2" />
                Download {pageTitle}
              </Button>
              {csvData && (
                <CSVLink
                  data={csvData}
                  filename={csvFileName}
                  className="hidden"
                  target="_blank"
                  ref={csvLink}
                />
              )}
            </div>
          </div>

          <div className="w-full text-center mt-4 bg-yellow-25 p-4 rounded-lg mb-10">
            <Text variant={TypographyStyles.H2}>
              {user?.financialProfile?.businessName ||
                `${user?.firstName} ${user?.lastName}`}
            </Text>
            <Text variant={TypographyStyles.H3}>{pageTitle} Statement</Text>
            <Text variant={TypographyStyles.BODY_SM}>
              for the period {startDate} — {endDate}
            </Text>
          </div>
        </div>
        <ProfitAndLossReports
          startDate={startDate}
          endDate={endDate}
          profitsObj={profitsObj}
          expensesObj={expensesObj}
          uncategorizedObj={uncategorizedObj}
          otherObj={otherObj}
          fetchingData={false}
          userIdForAdmin={userId}
          confirmed={confirmed}
        />
      </div>
    </div>
  )
}
